import React, { useState, useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function GravityPressureBalance() {
  const [gravity, setGravity] = useState<number>(5);
  const [pressure, setPressure] = useState<number>(5);

  const handleBalance = () => {
    setGravity(5);
    setPressure(5);
  };

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2;

    // Clear background
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // Calculate Imbalance & Tilt Angle
    const imbalance = gravity - pressure; // range -10 to 10
    // Capped angle at ±0.5 radians
    const targetAngle = Math.max(-0.5, Math.min(0.5, imbalance * 0.08));
    
    // Smooth angle transition
    const beamAngle = targetAngle; 

    // Pivot center
    const pivotY = cy - 20;

    // 1. Draw Pivot stand
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(cx, pivotY);
    ctx.lineTo(cx, pivotY + 110);
    ctx.stroke();

    // Base of stand
    ctx.beginPath();
    ctx.moveTo(cx - 40, pivotY + 110);
    ctx.lineTo(cx + 40, pivotY + 110);
    ctx.stroke();

    // 2. Draw Tilted Beam
    ctx.save();
    ctx.translate(cx, pivotY);
    ctx.rotate(beamAngle);

    // Beam rectangle
    ctx.fillStyle = '#E2E8F0';
    ctx.beginPath();
    ctx.roundRect(-120, -4, 240, 8, 4);
    ctx.fill();

    // Left and Right hanging points
    const lx = -120;
    const ly = 0;
    const rx = 120;
    const ry = 0;

    ctx.restore();

    // 3. Compute absolute positions of pans
    // lx in absolute: cx - 120 * cos(angle), ly in absolute: pivotY - 120 * sin(angle)
    const absLx = cx - 120 * Math.cos(beamAngle);
    const absLy = pivotY - 120 * Math.sin(beamAngle);

    const absRx = cx + 120 * Math.cos(beamAngle);
    const absRy = pivotY + 120 * Math.sin(beamAngle);

    // Pans hang vertically downward by 40px
    const panHeight = 46;
    const lPanCx = absLx;
    const lPanCy = absLy + panHeight;

    const rPanCx = absRx;
    const rPanCy = absRy + panHeight;

    // Helper to draw hanging strings & pan bowl
    const drawPan = (px: number, py: number, hx: number, hy: number) => {
      // Hanging strings
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(hx, hy);
      ctx.lineTo(px - 18, py);
      ctx.moveTo(hx, hy);
      ctx.lineTo(px + 18, py);
      ctx.stroke();

      // Pan bowl
      ctx.strokeStyle = '#94A3B8';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(px, py, 18, 0, Math.PI);
      ctx.stroke();
    };

    drawPan(lPanCx, lPanCy, absLx, absLy);
    drawPan(rPanCx, rPanCy, absRx, absRy);

    // 4. Draw Gravity arrows (Left pan - Red, downward)
    const drawArrow = (x: number, y: number, length: number, isDown: boolean, color: string) => {
      if (length <= 2) return;
      const endY = isDown ? y + length : y - length;
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, endY);
      ctx.stroke();

      // Arrowhead
      ctx.fillStyle = color;
      ctx.beginPath();
      if (isDown) {
        ctx.moveTo(x, endY);
        ctx.lineTo(x - 5, endY - 6);
        ctx.lineTo(x + 5, endY - 6);
      } else {
        ctx.moveTo(x, endY);
        ctx.lineTo(x - 5, endY + 6);
        ctx.lineTo(x + 5, endY + 6);
      }
      ctx.closePath();
      ctx.fill();
    };

    // Left Pan Gravity Force (Red)
    const gravityArrowHeight = (gravity / 10) * 35;
    const redColor = '#F87171';
    drawArrow(lPanCx - 9, lPanCy - 2, gravityArrowHeight, true, redColor);
    drawArrow(lPanCx, lPanCy - 5, gravityArrowHeight * 1.2, true, redColor);
    drawArrow(lPanCx + 9, lPanCy - 2, gravityArrowHeight, true, redColor);

    // Right Pan Thermal Pressure Force (Blue)
    const pressureArrowHeight = (pressure / 10) * 35;
    const blueColor = '#60A5FA';
    // Point UP from the pan base
    drawArrow(rPanCx - 9, rPanCy + 5, pressureArrowHeight, false, blueColor);
    drawArrow(rPanCx, rPanCy + 8, pressureArrowHeight * 1.2, false, blueColor);
    drawArrow(rPanCx + 9, rPanCy + 5, pressureArrowHeight, false, blueColor);

    // 5. Center Pivot knob
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(cx, pivotY, 6, 0, Math.PI * 2);
    ctx.fill();

    // 6. Hydrostatic Equilibrium Status label
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    if (Math.abs(imbalance) < 0.5) {
      ctx.fillStyle = '#22C55E'; // Green
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('⚖ HYDROSTATIC EQUILIBRIUM', cx, pivotY - 70);
      
      ctx.fillStyle = 'rgba(34, 197, 94, 0.7)';
      ctx.font = '11px sans-serif';
      ctx.fillText('Gravity & Fusion Pressure are balanced. Star is stable!', cx, pivotY - 50);
    } else if (imbalance > 0.5) {
      ctx.fillStyle = '#F87171'; // Red
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('💥 GRAVITY WINS — Collapsing!', cx, pivotY - 70);

      ctx.fillStyle = 'rgba(248, 113, 113, 0.7)';
      ctx.font = '11px sans-serif';
      ctx.fillText('Star contracts under its own weight.', cx, pivotY - 50);
    } else {
      ctx.fillStyle = '#60A5FA'; // Blue
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('💨 PRESSURE WINS — Expanding!', cx, pivotY - 70);

      ctx.fillStyle = 'rgba(96, 165, 250, 0.7)';
      ctx.font = '11px sans-serif';
      ctx.fillText('Radiation blows outer shell layers outwards.', cx, pivotY - 50);
    }

    // Top-Left Simulation Info Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('Gravity vs. Thermal Fusion Pressure Balance', 12, 24);
  }, [gravity, pressure]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Sliders and Balance button overlaid at the bottom */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex items-center justify-between gap-4">
        
        {/* Left Slider: Gravity */}
        <div className="flex flex-col gap-1 flex-1">
          <span className="text-[10px] uppercase font-bold text-rose-400">
            Gravity Force: {gravity.toFixed(1)}
          </span>
          <input
            type="range"
            min="0"
            max="10"
            step="0.1"
            value={gravity}
            onChange={(e) => setGravity(parseFloat(e.target.value))}
            className="w-full accent-rose-500 cursor-pointer h-1 bg-white/20 rounded-lg appearance-none"
          />
        </div>

        {/* Balance Button */}
        <button
          onClick={handleBalance}
          className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] uppercase font-bold tracking-wider transition-colors shrink-0"
        >
          Balance!
        </button>

        {/* Right Slider: Pressure */}
        <div className="flex flex-col gap-1 flex-1">
          <span className="text-[10px] uppercase font-bold text-blue-400">
            Fusion Pressure: {pressure.toFixed(1)}
          </span>
          <input
            type="range"
            min="0"
            max="10"
            step="0.1"
            value={pressure}
            onChange={(e) => setPressure(parseFloat(e.target.value))}
            className="w-full accent-blue-500 cursor-pointer h-1 bg-white/20 rounded-lg appearance-none"
          />
        </div>
      </div>
    </div>
  );
}
